import axios from 'axios';

const base = process.env.REACT_APP_API_URL || 'https://football.olympikesoft.com';

const api = axios.create({
  baseURL: `${base.replace(/\/+$/, '')}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor de Pedidos: Adiciona o token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
      config.headers['Authorization'] = `Bearer ${cleanToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de Respostas
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Opcional: tratar logout automático
    }
    return Promise.reject(error);
  }
);

// 1. Authentication
export const authAPI = {
  register: async (registrationData) => {
    const response = await api.post('/auth/register', registrationData);
    if (response.data && response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data && response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  googleAuth: async (idToken, teamName, marketingConsent) => {
    const response = await api.post('/auth/google', { idToken, teamName, marketingConsent });
    if (response.data && response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Inside a game portal "/" is the portal's page, not ours: reload into the game instead.
    if (process.env.REACT_APP_PORTAL) { window.location.reload(); return; }
    window.location.href = '/'; 
  }
};

// 2. User Profile Management
export const usersAPI = {
  getMyProfile: () => api.get('/users/me'),
  updateMyProfile: (email) => api.put('/users/me', { email }),
  deleteMyAccount: (password) => api.delete('/users/me', { data: { password } })
};

// 3. Team Management
export const teamAPI = {
  getTeamDetails: () => api.get('/team'),
  updateTeamDetails: (teamData) => api.put('/team', teamData),
  saveFormations: (formations) => api.put('/team/formations', { formations }),
  getTransactionHistory: (params) => api.get('/team/transactions', { params }),
  getMatchHistory: (params) => api.get('/team/matches', { params }),
  
  // Atualizado: Suporta agora filtros 'search', 'position' e 'detailed_position'
  getTeamPlayers: (params) => api.get('/team/players', { params }),
  
  // Novo: Estatísticas de performance (Top Scorers, Assists, Defenders, etc)
  getTeamScore: () => api.get('/team/score'),

  setTeamLineup: (lineupData) => api.post('/team/lineup', lineupData),
  
  // Tactics
  getTeamTactics: () => api.get('/team/tactics'),
  updateTeamTactics: (body) => api.put('/team/tactics', body),
  updateSetPieces: (body) => api.put('/team/tactics/set-pieces', body),
  updateTakers: (body) => api.put('/team/tactics/takers', body),

  // Suspensions
  getSuspensions: () => api.get('/team/suspensions'),
  getSuspensionHistory: (params) => api.get('/team/suspensions/history', { params })
};

// 4. Player Management & Training
export const playersAPI = {
  getPlayerDetails: (playerId) => api.get(`/player/${playerId}`),
  trainPlayer: (playerId, statToTrain) => api.post(`/player/${playerId}/train`, { statToTrain }),
  getTrainingInfo: (playerId) => api.get(`/player/${playerId}/training`),
};

// 5. Match Invitations
export const matchesAPI = {
  getCoaches: (params) => api.get('/match/coaches', { params }),
  inviteTeam: (opponent_team_id, match_date) => api.post('/match/invite', { opponent_team_id, match_date }),
  getInvitations: (params) => api.get('/match/invitations', { params }),
  acceptInvitation: (invitationId) => api.put(`/match/invitations/${invitationId}/accept`),
  rejectInvitation: (invitationId) => api.put(`/match/invitations/${invitationId}/reject`),
  cancelInvitation: (invitationId) => api.delete(`/match/invitations/${invitationId}`)
};

// 6. Transfer Market
export const marketAPI = {
  /**
   * Atualizado: Agora suporta busca dinâmica avançada.
   * Params: { limit, offset, search, position, detailed_position, min_price, max_price, min_age, max_age, sort_by, order }
   */
  browseMarket: (params) => api.get('/market', { params }),
  listPlayerForSale: (playerId, price) => api.put(`/market/players/${playerId}/list`, { price }),
  unlistPlayer: (playerId) => api.put(`/market/players/${playerId}/unlist`),
  buyPlayer: (playerId) => api.post(`/market/players/${playerId}/buy`),
  quickSell: (playerId) => api.post(`/market/players/${playerId}/quick-sell`),
};

// Notificações
export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  remove: (id) => api.delete(`/notifications/${id}`),
};

// 7. Challenge System
export const challengeAPI = {
  setAvailability: (isAvailable) => api.put('/challenge/availability', { is_available: isAvailable }),
  getTeamStatus: () => api.get('/challenge/status'),
  getAvailableTeams: (params) => api.get('/challenge/available-teams', { params }),
  sendChallenge: (opponentTeamId) => api.post('/challenge/send', { opponent_team_id: opponentTeamId }),
  sendRandomChallenge: () => api.post('/challenge/random'),
  getLiveMatches: () => api.get('/challenge/matches/live'),
  getMatchHistory: (params) => api.get('/challenge/matches/history', { params }),
  getMatchDetails: (matchId) => api.get(`/challenge/matches/${matchId}`),
  getLeagues: () => api.get('/challenge/leagues'),
  getLeaderboard: (params) => api.get('/challenge/leaderboard', { params }),
  getMyLeaderboardStats: () => api.get('/challenge/leaderboard/me'),
  buyExtraChallenge: (currency = 'coins') => api.post('/challenge/buy-extra', { currency })
};

// 8. Friends System
export const friendsAPI = {
  // Aceita email ou username do treinador — deteta pelo '@'
  sendRequest: (identifier) =>
    api.post('/friends/request', identifier.includes('@')
      ? { receiverEmail: identifier.trim() }
      : { receiverUsername: identifier.trim() }),
  getPendingRequests: () => api.get('/friends/requests/pending'),
  getSentRequests: () => api.get('/friends/requests/sent'),
  acceptRequest: (requestId) => api.post(`/friends/requests/${requestId}/accept`),
  rejectRequest: (requestId) => api.post(`/friends/requests/${requestId}/reject`),
  cancelRequest: (requestId) => api.delete(`/friends/requests/${requestId}`),
  getFriends: () => api.get('/friends'),
  removeFriend: (friendUserId) => api.delete(`/friends/${friendUserId}`),
  getAvailableForMatch: () => api.get('/friends/available-for-match'),
  getFriendsLeaderboard: () => api.get('/friends/leaderboard'),
  getH2H: (friendUserId) => api.get(`/friends/${friendUserId}/h2h`),
};

// 9. Global Leaderboards
export const leaderboardAPI = {
  getGlobalLeaderboard: (params) => api.get('/leaderboard', { params }),
  getGoalsLeaderboard: (params) => api.get('/leaderboard/goals', { params }),
  getAssistsLeaderboard: (params) => api.get('/leaderboard/assists', { params }),
  getRatingLeaderboard: (params) => api.get('/leaderboard/rating', { params }),
  getStatsLeaderboard: (params) => api.get('/leaderboard/stats', { params }),
  getTeamsLeaderboard: (params) => api.get('/leaderboard/teams', { params }),
  getDefenseLeaderboard: (params) => api.get('/leaderboard/defense', { params }),
  getMyRank: () => api.get('/leaderboard/me')
};

// 9b. Activity Feed
export const feedAPI = {
  getFeed: (params) => api.get('/feed', { params })
};

// 10. Gamification
export const gamificationAPI = {
  getDailyBonusInfo: () => api.get('/gamification/daily-bonus'),
  claimDailyBonus: () => api.post('/gamification/daily-bonus')
};

// 10b. Missions
export const missionsAPI = {
  getMissions: () => api.get('/missions'),
  claimReward: (missionId) => api.post(`/missions/claim/${missionId}`),
  claimAll: () => api.post('/missions/claim-all'),
};

// 10c. Engagement
export const engagementAPI = {
  getProfile: () => api.get('/engagement/profile'),
  getTraining: () => api.get('/engagement/training'),
  setTraining: (focus) => api.post('/engagement/training', { focus }),
  teamTalk: (talk_type, match_id) => api.post('/engagement/team-talk', { talk_type, match_id }),
  getTeamTalks: () => api.get('/engagement/team-talks'),
  getObjectives: () => api.get('/engagement/objectives'),
  claimObjective: (id) => api.post(`/engagement/objectives/${id}/claim`),
  getMorale: () => api.get('/engagement/morale'),
};

// 10d. Season (personal division vs AI clubs) + Board
export const seasonAPI = {
  get: () => api.get('/season'),
  play: () => api.post('/season/play'),
  next: () => api.post('/season/next'),
};

// 10e. Challenge links (play a friend's club before signing up)
export const playLinksAPI = {
  create: () => api.post('/play-links'),
  get: (code) => api.get(`/play-links/${code}`),
  play: (code, body = {}) => api.post(`/play-links/${code}/play`, body),
  claim: (body) => api.post('/play-links/claim', body),
  // Portal builds: a guest club without a challenge link.
  startGuest: (body = {}) => api.post('/play-links/guest', body),
};

// 11. Store & Monetization
export const storeAPI = {
  getCoinPacks: () => api.get('/store/packs'),
  getItems: (params) => api.get('/store/items', { params }),
  getBalance: () => api.get('/store/balance'),
  createCheckout: (packId) => api.post('/store/checkout', { pack_id: packId }),
  getCheckoutStatus: (sessionId) => api.get(`/store/checkout/${sessionId}/status`),
  buyItem: (itemId, body) => api.post(`/store/items/${itemId}/buy`, body),
  getPurchaseHistory: (params) => api.get('/store/history', { params }),
  getPaymentHistory: () => api.get('/store/payments'),
  getActiveBoosts: () => api.get('/store/boosts'),
  convertCoinsToGold: (coinsToConvert) => api.post('/store/convert', { coins_to_convert: coinsToConvert }),
  payLeagueCreation: () => api.post('/store/pay-league-creation'),
  redeemRenameTeam: (newName) => api.post('/store/redeem/rename-team', { new_name: newName }),
  redeemRenameCoach: (newName) => api.post('/store/redeem/rename-coach', { new_name: newName }),
};

// 12. Achievements
export const achievementsAPI = {
  list: () => api.get('/achievements'),
  claimReward: (achievementId) => api.post(`/achievements/${achievementId}/claim`),
};

// 13. Push Notifications
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
};

// 14. Private Leagues
export const privateLeaguesAPI = {
  create: (payload) => api.post('/private-leagues', payload),
  list: () => api.get('/private-leagues'),
  getStandings: (leagueId) => api.get(`/private-leagues/${leagueId}`),
  join: (inviteCode) => api.post('/private-leagues/join', { invite_code: inviteCode }),
  challenge: (leagueId, opponentUserId) => api.post(`/private-leagues/${leagueId}/challenge`, { opponent_user_id: opponentUserId }),
  getMatches: (leagueId, params) => api.get(`/private-leagues/${leagueId}/matches`, { params }),
  leave: (leagueId) => api.delete(`/private-leagues/${leagueId}/leave`),
  remove: (leagueId) => api.delete(`/private-leagues/${leagueId}`),
  kickMember: (leagueId, userId) => api.delete(`/private-leagues/${leagueId}/members/${userId}`),
};

export const referralAPI = {
  getMyReferrals: () => api.get('/referrals'),
  validateCode: (code) => api.get(`/referrals/validate/${code}`),
};

export const tournamentAPI = {
  list: (params) => api.get('/tournaments', { params }),
  get: (id) => api.get(`/tournaments/${id}`),
  join: (id) => api.post(`/tournaments/${id}/join`),
  leave: (id) => api.post(`/tournaments/${id}/leave`),
};

const apiServices = {
  auth: authAPI,
  users: usersAPI,
  team: teamAPI,
  players: playersAPI,
  matches: matchesAPI,
  challenge: challengeAPI,
  market: marketAPI,
  friends: friendsAPI,
  leaderboard: leaderboardAPI,
  gamification: gamificationAPI,
  missions: missionsAPI,
  engagement: engagementAPI,
  store: storeAPI,
  achievements: achievementsAPI,
  push: pushAPI,
  privateLeagues: privateLeaguesAPI,
  tournaments: tournamentAPI,
  getMatchStreamUrl: (matchId) => {
    const token = localStorage.getItem('token');
    const cleanToken = token ? (token.startsWith('Bearer ') ? token.slice(7) : token) : '';
    return `${api.defaults.baseURL}/match/stream/${matchId}?token=${cleanToken}`;
  }
};

export default apiServices;